import config from "../config";
import HttpClient from "./httpclient";

export function s3DownloadUrl(path: string): string {
  return `${config.api}/documents/s3?file=${encodeURIComponent(path)}`;
}

function safeFileName(value: string | null | undefined): string {
  const name = String(value || "download").replace(/[^A-Za-z0-9 ._()-]/g, "_");
  return name.slice(0, 180) || "download";
}

function responseFileName(contentDisposition: unknown): string | null {
  if (typeof contentDisposition !== "string") return null;
  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      return utf8;
    }
  }
  return contentDisposition.match(/filename="?([^";]+)"?/i)?.[1]?.trim() || null;
}

export async function fetchAuthenticatedBlobUrl(
  url: string,
  options: { contentType?: string; fileName?: string } = {},
): Promise<{ fileName: string | null; url: string }> {
  const response = await HttpClient.get(url, { responseType: "blob" });
  const contentType = response.headers?.["content-type"];
  const blob = options.contentType
    ? new Blob([response.data], { type: options.contentType })
    : response.data instanceof Blob
      ? response.data
      : new Blob([response.data], typeof contentType === "string" ? { type: contentType } : undefined);
  const fileName = responseFileName(response.headers?.["content-disposition"]) || options.fileName || null;
  // A nameless blob URL has no filename, so Chrome titles the PDF tab "about:blank".
  const named = fileName
    ? new File([blob], safeFileName(fileName), { type: blob.type || "application/octet-stream" })
    : blob;
  return {
    fileName,
    url: URL.createObjectURL(named),
  };
}

export async function downloadAuthenticatedUrl(url: string, fallbackName?: string): Promise<void> {
  const downloaded = await fetchAuthenticatedBlobUrl(url, { fileName: fallbackName });
  const link = document.createElement("a");
  link.href = downloaded.url;
  link.download = safeFileName(downloaded.fileName || fallbackName || url.split(/[/?#]/).filter(Boolean).pop());
  link.style.display = "none";
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloaded.url), 0);
  }
}
